import React from 'react';
import { Calendar, Link2, GraduationCap, ArrowRight } from 'lucide-react';
import { LogoFull } from '../components/Logo';

const APPS = [
  {
    id: 'event-planner',
    label: 'Event Planner',
    description: 'Manage corporate meetup logistics — venues, attendees, schedule, meals, budget, and more.',
    icon: Calendar,
    accentColor: '#f96516',
    bgGradient: 'linear-gradient(135deg, rgba(249,101,22,0.13) 0%, rgba(255,138,61,0.07) 100%)',
    borderColor: 'rgba(249,101,22,0.2)',
    roles: ['admin', 'employee'],
  },
  {
    id: 'links-viewer',
    label: 'Links Viewer',
    description: 'Central hub for important links across teams — docs, tools, dashboards, and references.',
    icon: Link2,
    accentColor: '#7c3aed',
    bgGradient: 'linear-gradient(135deg, rgba(124,58,237,0.11) 0%, rgba(139,92,246,0.05) 100%)',
    borderColor: 'rgba(124,58,237,0.2)',
    roles: ['admin'],
    adminOnly: true,
  },
  {
    id: 'onboarding-guide',
    label: 'Engineer Onboarding',
    description: 'Run a new engineer’s 15-day trial — tasks, daily progress, mentor reviews and contract e-signing.',
    icon: GraduationCap,
    accentColor: '#0ea5e9',
    bgGradient: 'linear-gradient(135deg, rgba(14,165,233,0.11) 0%, rgba(56,189,248,0.05) 100%)',
    borderColor: 'rgba(14,165,233,0.2)',
    roles: ['admin', 'employee'],
  },
];

export default function Landing({ userName, userRole, appAccess = {}, onSelect }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = userName?.split(' ')[0] || 'there';

  // An explicit appAccess entry (per-email grant) overrides the role check
  const visibleApps = APPS.filter(app =>
    app.id in appAccess ? appAccess[app.id] : app.roles.includes(userRole)
  );
  const columns = Math.min(visibleApps.length, 3);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 24px',
      gap: 48,
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <LogoFull height={40} />
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0, lineHeight: 1.2 }}>
            {greeting}, {firstName} 👋
          </h1>
          <p style={{ color: 'var(--text2)', marginTop: 8, fontSize: 14, margin: '8px 0 0' }}>
            Choose an application to continue
          </p>
        </div>
      </div>

      {/* App cards grid — 1 column on mobile, up to `columns` on wider screens */}
      <div
        className={`grid grid-cols-1 ${columns >= 2 ? 'sm:grid-cols-2' : ''} ${columns >= 3 ? 'lg:grid-cols-3' : ''}`}
        style={{
          gap: 20,
          width: '100%',
          maxWidth: columns >= 3 ? 960 : 680,
        }}
      >
        {visibleApps.map(app => {
          const Icon = app.icon;
          return (
            <AppCard
              key={app.id}
              app={app}
              Icon={Icon}
              showAdminBadge={Boolean(app.adminOnly) && userRole === 'admin'}
              onSelect={onSelect}
            />
          );
        })}
      </div>

      {/* Footer */}
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
        Nativeorange · Internal Platform
      </p>
    </div>
  );
}

function AppCard({ app, Icon, showAdminBadge, onSelect }) {
  const [hovered, setHovered] = React.useState(false);
  const disabled = Boolean(app.comingSoon);
  const active = hovered && !disabled;

  return (
    <button
      onClick={() => !disabled && onSelect(app.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: app.bgGradient,
        border: `1.5px solid ${active ? app.accentColor + '50' : app.borderColor}`,
        borderRadius: 20,
        padding: '28px 24px',
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.2s ease',
        backdropFilter: 'blur(20px)',
        boxShadow: active
          ? `0 20px 48px rgba(0,0,0,0.1), 0 0 0 1px ${app.accentColor}20`
          : '0 4px 20px rgba(0,0,0,0.05)',
        transform: active ? 'translateY(-3px)' : 'translateY(0)',
        width: '100%',
      }}
    >
      {/* Icon row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div style={{
          width: 48, height: 48,
          borderRadius: 14,
          background: `${app.accentColor}18`,
          border: `1.5px solid ${app.accentColor}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}>
          <Icon size={22} color={app.accentColor} />
        </div>
        {(app.comingSoon || showAdminBadge) && (
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em',
            color: app.comingSoon ? 'var(--text2)' : app.accentColor,
            background: app.comingSoon ? 'rgba(107,114,128,0.1)' : `${app.accentColor}12`,
            border: app.comingSoon ? '1px solid rgba(107,114,128,0.25)' : `1px solid ${app.accentColor}25`,
            padding: '3px 10px', borderRadius: 20,
          }}>{app.comingSoon ? 'Coming Soon' : 'Admin'}</span>
        )}
      </div>

      {/* Text */}
      <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)', marginBottom: 8 }}>
        {app.label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 22 }}>
        {app.description}
      </div>

      {/* CTA */}
      {disabled ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text3)', fontWeight: 700, fontSize: 13 }}>
          Coming soon
        </div>
      ) : (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          color: app.accentColor, fontWeight: 700, fontSize: 13,
          transition: 'gap 0.2s',
          gap: active ? 10 : 6,
        }}>
          Open app <ArrowRight size={14} />
        </div>
      )}
    </button>
  );
}
